import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input, Button } from '../../components/common';
import './UserProfile.css';

export default function UserProfileDetails() {
  const navigate = useNavigate();

  const [userType, setUserType] = useState('USER');
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [hasPartner, setHasPartner] = useState(false); // 👈 new
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  // ✅ FETCH LIVE USER DATA ON MOUNT
  useEffect(() => {
    fetchUserProfile();
  }, []);

  const getAuthToken = () => {
    return (
      localStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('jwt_token')
    );
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAuthToken();

      if (!token) {
        setError('Please login to view profile');
        setLoading(false);
        return;
      }

      const url = `${API_BASE}/api/account/profile`;
      console.log('🔥 Fetching profile from:', url);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('✅ Profile data:', response.data);

      const user = response.data.user || response.data.data || response.data;

      if (!user) {
        setError('No user data found in response');
        return;
      }

      const fullName =
        user.fullName ||
        [user.firstName, user.lastName].filter(Boolean).join(' ') ||
        user.name ||
        '';

      const phone =
        user.mobile || user.phone || user.phoneNumber || user.contactNumber || '';

      const email = user.email || '';

      setProfileData({
        name: fullName,
        phone: phone,
        email: email,
      });

      // 👇 pick up hasPartner from backend (defaults to false)
      setHasPartner(!!response.data.hasPartner);
    } catch (err) {
      console.error(
        '❌ Profile fetch error:',
        err.response?.data || err.message
      );
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => navigate('/home');

  const handleUserTypePartner = () => {
    setUserType('PARTNER');
    navigate('/partnerprofile');
  };

  const handleUserTypeChange = () => {
    setUserType('USER');
    navigate('/profile');
  };

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateProfile = () => {
    setEditMode(true);
  };

  const handleSaveProfile = async () => {
    try {
      const token =
        localStorage.getItem('authToken') ||
        localStorage.getItem('token') ||
        localStorage.getItem('jwt_token');

      if (!token) {
        alert('Please login again');
        return;
      }

      setSaving(true);
      setError(null);

      // We only allow editing name + email; phone stays read-only
      const payload = {
        name: profileData.name,
        email: profileData.email,
      };

      const url = `${API_BASE}/api/account/profile`;
      console.log('📡 Updating profile via:', url, 'payload:', payload);

      const response = await axios.put(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('✅ Profile updated:', response.data);
      setEditMode(false);

      // Refresh data from server to ensure we are in sync with Mongo
      await fetchUserProfile();
    } catch (err) {
      console.error(
        '❌ Update failed:',
        err.response?.data || err.message
      );
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to update profile'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="profile-details-root"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div style={{ color: '#A4E376', fontSize: '1.2rem' }}>
          Loading profile...
        </div>
      </div>
    );
  }

  return (
    <div className="profile-details-root">
      {/* HEADER SECTION */}
      <div className="profile-details-header">
        <button className="profile-details-back-btn" onClick={handleBack}>
          <i className="fas fa-arrow-left"></i>
        </button>

        <div className="profile-details-avatar-section">
          <div className="profile-details-avatar">
            <i className="fas fa-user"></i>
          </div>
          <h1 className="profile-details-header-title">My Profile</h1>
        </div>

        {/* Toggle buttons:
            - Always show USER
            - Only show PARTNER if hasPartner = true */}
        <div className="profile-details-toggle-group">
          <button
            className={`profile-details-toggle-btn ${
              userType === 'USER' ? 'active' : ''
            }`}
            onClick={handleUserTypeChange}
          >
            USER
          </button>

          {hasPartner && (
            <button
              className={`profile-details-toggle-btn ${
                userType === 'PARTNER' ? 'active' : ''
              }`}
              onClick={handleUserTypePartner}
            >
              PARTNER
            </button>
          )}
        </div>
      </div>

      {/* FORM SECTION */}
      <div className="profile-details-form-section">
        {error && (
          <div
            style={{
              color: '#FF3333',
              textAlign: 'center',
              marginBottom: '1rem',
            }}
          >
            {error}
          </div>
        )}

        {/* Name Field */}
        <div
          className={`profile-details-field-group${
            editMode ? ' editable' : ''
          }`}
        >
          <div className="profile-details-field-icon">
            <i className="fas fa-user"></i>
          </div>
          <Input
            type="text"
            placeholder="Full Name"
            className={`profile-details-input${
              editMode ? ' edit-bg' : ''
            }`}
            value={profileData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            disabled={!editMode}
          />
        </div>

        {/* Phone Field (Always ReadOnly) */}
        <div className="profile-details-field-group">
          <div className="profile-details-field-icon">
            <i className="fas fa-phone"></i>
          </div>
          <Input
            type="tel"
            placeholder="Phone Number"
            className="profile-details-input"
            value={profileData.phone}
            disabled
          />
        </div>

        {/* Email Field */}
        <div
          className={`profile-details-field-group${
            editMode ? ' editable' : ''
          }`}
        >
          <div className="profile-details-field-icon">
            <i className="fas fa-envelope"></i>
          </div>
          <Input
            type="email"
            placeholder="Email Address"
            className={`profile-details-input${
              editMode ? ' edit-bg' : ''
            }`}
            value={profileData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            disabled={!editMode}
          />
        </div>

        {/* Update / Save Button */}
        {!editMode ? (
          <button
            className="profile-details-update-link"
            onClick={handleUpdateProfile}
          >
            Update Profile
          </button>
        ) : (
          <Button
            className="profile-details-update-save-btn"
            style={{
              background: '#89D957',
              color: '#062540',
              fontWeight: 700,
              fontSize: '1.08rem',
              padding: '0.8rem 2.2rem',
              borderRadius: '1.5rem',
              border: 'none',
              marginTop: '1.5rem',
            }}
            onClick={handleSaveProfile}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </div>
    </div>
  );
}
